import { ArrowRight, Activity } from 'lucide-react';
import { useApp } from '@/hooks/use-app-context';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { contributeSelectTriggerClass, contributeSelectContentClass, contributeWarmActionClass } from '@/components/contribute/session-control-styles';
import { ContributionInfoStrip } from '@/components/contribute/ContributionMetricStrip';
import type { AssessmentTask, ModelItem } from './model-performance.types';
import { isAssessmentTask } from './model-performance.helpers';

interface ModelPerformanceSetupProps {
  selectedTask: AssessmentTask;
  selectedModel: string;
  selectedLanguage: string;
  models: ModelItem[];
  loadError: string | null;
  isLoading: boolean;
  isLoadingLanguages: boolean;
  activeLanguages: Array<{ code: string; name: string; displayName?: string; flag?: string }>;
  onSelectTask: (task: AssessmentTask) => void;
  onSelectModel: (model: string) => void;
  onSelectLanguage: (lang: string) => void;
  onStart: () => void;
  onRetry: () => void;
  onClearError: () => void;
  onGoHome: () => void;
}

export function ModelPerformanceSetup({
  selectedTask,
  selectedModel,
  selectedLanguage,
  models,
  loadError,
  isLoading,
  isLoadingLanguages,
  activeLanguages,
  onSelectTask,
  onSelectModel,
  onSelectLanguage,
  onStart,
  onRetry,
  onClearError,
  onGoHome,
}: ModelPerformanceSetupProps) {
  const { t } = useApp();
  const tModel = t.modelPerformancePage;
  const availableModels = models.filter((m) => m.task === selectedTask);
  const taskName = tModel.taskNames[selectedTask] ?? selectedTask;
  const availableModelsLabel = tModel.availableModels
    .replace('{count}', String(availableModels.length))
    .replace('{task}', taskName);

  return (
    <div>
      <div className="max-w-6xl">
        <div className="grid w-full gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <div className="space-y-7">
            <ContributionInfoStrip
              className="max-w-xl"
              items={[
                { eyebrow: String(t.taskLabel ?? t.sessionValidation ?? ''), text: String(t.selectLanguage ?? ''), tone: 'emerald' },
                { eyebrow: String(availableModels.length), text: availableModelsLabel, tone: 'emerald' },
                { eyebrow: String(t.selectLanguage ?? ''), text: selectedLanguage || '—', tone: 'emerald' },
              ]}
            />
          </div>

          <div className="rounded-2xl border border-border/40 bg-background/60 p-4 sm:p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">{String(t.selectLanguage ?? '')}</p>
                <h2 className="text-xl font-semibold tracking-tight text-foreground">{String(t.sessionValidation ?? '')}</h2>
              </div>
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-primary/16 bg-primary/[0.055] text-primary">
                <Activity className="h-5 w-5" />
              </div>
            </div>

            <Select
              value={selectedTask}
              onValueChange={(value) => {
                if (isAssessmentTask(value)) onSelectTask(value);
              }}
            >
              <SelectTrigger className={contributeSelectTriggerClass}>
                <SelectValue placeholder={String(t.taskLabel ?? '')} />
              </SelectTrigger>
              <SelectContent className={contributeSelectContentClass}>
                <SelectItem value="TTS">{tModel.taskOptions.TTS}</SelectItem>
                <SelectItem value="ASR">{tModel.taskOptions.ASR}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedModel} onValueChange={onSelectModel} disabled={!selectedTask}>
              <SelectTrigger className={contributeSelectTriggerClass}>
                <SelectValue placeholder={String(tModel.selectModelPlaceholder ?? '')} />
              </SelectTrigger>
              <SelectContent className={contributeSelectContentClass}>
                {availableModels.map((model) => (
                  <SelectItem key={model.name} value={model.name}>
                    {model.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLanguage} onValueChange={onSelectLanguage}>
              <SelectTrigger className={contributeSelectTriggerClass}>
                <SelectValue placeholder={String(t.selectLanguage ?? '')} />
              </SelectTrigger>
              <SelectContent className={contributeSelectContentClass}>
                {isLoadingLanguages ? (
                  <SelectItem value="__loading__" disabled>{tModel.loadingLanguages}</SelectItem>
                ) : activeLanguages.length === 0 ? (
                  <SelectItem value="__none__" disabled>{tModel.noLanguagesAvailable}</SelectItem>
                ) : (
                  activeLanguages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.flag ? `${lang.flag} ${lang.displayName || lang.name}` : (lang.displayName || lang.name)}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>

            {loadError && (
              <div className="rounded-xl border border-destructive/25 bg-destructive/[0.04] p-4 space-y-3">
                <p className="text-sm text-destructive">{loadError}</p>
                <div className="flex gap-2">
                  <Button onClick={onRetry} size="sm" className="rounded-full">{String(t.startAssessment ?? '')}</Button>
                  <Button variant="outline" size="sm" onClick={onClearError} className="rounded-full">{String(t.cancel ?? '')}</Button>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2.5 pt-1 sm:flex-row sm:items-center sm:justify-between">
              <Button onClick={onGoHome} variant="ghost" size="sm" className="rounded-full border-border/35 bg-background/50 px-3 text-[0.68rem] font-semibold text-muted-foreground hover:text-foreground">
                {String(t.back ?? '')}
              </Button>
              <Button onClick={onStart} preventDoubleClick className={`${contributeWarmActionClass} w-full sm:w-auto`} disabled={!selectedLanguage || !selectedModel || !selectedTask || isLoading}>
                {isLoading && <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current/30 border-t-current" />}
                {isLoading ? String(t.loading ?? '') : String(t.startAssessment ?? '')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
